/**
 * Icon set — ported 1:1 from the Claude Design handoff (`dash-icons.jsx`,
 * originally exposed on `window.HL_ICONS`).
 *
 * Feather-style strokes that match the widget: every icon shares the same
 * `viewBox="0 0 24 24"`, `stroke-width: 1.9`, round line caps/joins, and uses
 * `currentColor` so the icon inherits the surrounding text color (the design
 * tokens drive that color via Tailwind utilities on the parent).
 *
 * A few glyphs are solid fills rather than strokes (sparkle, play, pause, dots)
 * — those set `fill="currentColor"` and `stroke="none"`, exactly as the design.
 *
 * Usage:
 *   import { Icon } from '../components/ui/icons';
 *   <Icon.inbox size={16} className="text-muted" />
 *
 * Each component is `React.memo`-wrapped so re-renders of a parent don't
 * re-render an unchanged icon.
 */
import { memo, type ReactElement, type SVGProps } from 'react';

export interface IconProps {
  /** Square dimension in px for both width and height. Defaults to 16. */
  size?: number;
  className?: string;
}

/**
 * Shared SVG attributes. Mirrors the design's `S(size, children, fill)` helper:
 * stroked icons inherit `currentColor` on the stroke; filled icons (`fill`)
 * inherit `currentColor` on the fill and drop the stroke.
 */
function svgAttrs(size: number | undefined, filled = false): SVGProps<SVGSVGElement> {
  const px = size ?? 16;
  return {
    width: px,
    height: px,
    viewBox: '0 0 24 24',
    fill: filled ? 'currentColor' : 'none',
    stroke: filled ? 'none' : 'currentColor',
    strokeWidth: 1.9,
    strokeLinecap: 'round',
    strokeLinejoin: 'round',
    'aria-hidden': true,
    focusable: false,
  };
}

/** Factory: build a memoized stroked icon from its inner SVG children. */
function strokeIcon(
  displayName: string,
  children: ReactElement | ReactElement[],
) {
  const Component = memo(function Icon({ size, className }: IconProps): ReactElement {
    return (
      <svg {...svgAttrs(size)} className={className}>
        {children}
      </svg>
    );
  });
  Component.displayName = `Icon.${displayName}`;
  return Component;
}

/** Factory: build a memoized solid-fill icon from its inner SVG children. */
function fillIcon(
  displayName: string,
  children: ReactElement | ReactElement[],
) {
  const Component = memo(function Icon({ size, className }: IconProps): ReactElement {
    return (
      <svg {...svgAttrs(size, true)} className={className}>
        {children}
      </svg>
    );
  });
  Component.displayName = `Icon.${displayName}`;
  return Component;
}

/* ── Individual icon components ──────────────────────────────────────────── */

export const Inbox = strokeIcon('inbox', [
  <path key="a" d="M22 12h-6l-2 3h-4l-2-3H2" />,
  <path
    key="b"
    d="M5.45 5.11 2 12v6a2 2 0 0 0 2 2h16a2 2 0 0 0 2-2v-6l-3.45-6.89A2 2 0 0 0 16.76 4H7.24a2 2 0 0 0-1.79 1.11z"
  />,
]);

export const Ticket = strokeIcon('ticket', [
  <path
    key="a"
    d="M3 7a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2v2a2 2 0 0 0 0 4v2a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-2a2 2 0 0 0 0-4z"
  />,
  <line key="b" x1="13" y1="5" x2="13" y2="19" strokeDasharray="2 3" />,
]);

export const Widgets = strokeIcon('widgets', [
  <rect key="a" x="3" y="3" width="7" height="7" rx="1.5" />,
  <rect key="b" x="14" y="3" width="7" height="7" rx="1.5" />,
  <rect key="c" x="14" y="14" width="7" height="7" rx="1.5" />,
  <rect key="d" x="3" y="14" width="7" height="7" rx="1.5" />,
]);

export const Clusters = strokeIcon('clusters', [
  <circle key="a" cx="7" cy="7" r="3.2" />,
  <circle key="b" cx="17.5" cy="8" r="2.4" />,
  <circle key="c" cx="9.5" cy="17" r="2.6" />,
  <line key="d" x1="9.6" y1="8.4" x2="15.4" y2="8" />,
  <line key="e" x1="8" y1="9.8" x2="9.2" y2="14.5" />,
]);

export const Org = strokeIcon('org', [
  <path key="a" d="M3 21h18" />,
  <path key="b" d="M5 21V7l8-4v18" />,
  <path key="c" d="M19 21V11l-6-4" />,
  <path key="d" d="M9 9h0M9 12h0M9 15h0" />,
]);

export const Search = strokeIcon('search', [
  <circle key="a" cx="11" cy="11" r="7" />,
  <line key="b" x1="21" y1="21" x2="16.65" y2="16.65" />,
]);

export const ChevDown = strokeIcon('chevDown', [
  <polyline key="a" points="6 9 12 15 18 9" />,
]);

export const ChevRight = strokeIcon('chevRight', [
  <polyline key="a" points="9 18 15 12 9 6" />,
]);

export const ArrowLeft = strokeIcon('arrowLeft', [
  <line key="a" x1="19" y1="12" x2="5" y2="12" />,
  <polyline key="b" points="12 19 5 12 12 5" />,
]);

export const Sun = strokeIcon('sun', [
  <circle key="a" cx="12" cy="12" r="4.2" />,
  <path
    key="b"
    d="M12 1v2M12 21v2M4.2 4.2l1.4 1.4M18.4 18.4l1.4 1.4M1 12h2M21 12h2M4.2 19.8l1.4-1.4M18.4 5.6l1.4-1.4"
  />,
]);

export const Moon = strokeIcon('moon', [
  <path key="a" d="M21 12.8A9 9 0 1 1 11.2 3 7 7 0 0 0 21 12.8z" />,
]);

export const Plus = strokeIcon('plus', [
  <line key="a" x1="12" y1="5" x2="12" y2="19" />,
  <line key="b" x1="5" y1="12" x2="19" y2="12" />,
]);

export const X = strokeIcon('x', [
  <line key="a" x1="18" y1="6" x2="6" y2="18" />,
  <line key="b" x1="6" y1="6" x2="18" y2="18" />,
]);

export const Check = strokeIcon('check', [
  <polyline key="a" points="20 6 9 17 4 12" />,
]);

export const Sparkle = fillIcon('sparkle', [
  <path key="a" d="M12 2l1.8 5.6L19.5 9l-5.7 1.4L12 16l-1.8-5.6L4.5 9l5.7-1.4z" />,
]);

export const Play = fillIcon('play', [
  <polygon key="a" points="6 4 20 12 6 20 6 4" />,
]);

export const Pause = fillIcon('pause', [
  <rect key="a" x="6" y="5" width="4" height="14" rx="1" />,
  <rect key="b" x="14" y="5" width="4" height="14" rx="1" />,
]);

export const Camera = strokeIcon('camera', [
  <path
    key="a"
    d="M23 19a2 2 0 0 1-2 2H3a2 2 0 0 1-2-2V8a2 2 0 0 1 2-2h4l2-3h6l2 3h4a2 2 0 0 1 2 2z"
  />,
  <circle key="b" cx="12" cy="13" r="3.6" />,
]);

export const Alert = strokeIcon('alert', [
  <path
    key="a"
    d="M10.29 3.86 1.82 18a2 2 0 0 0 1.71 3h16.94a2 2 0 0 0 1.71-3L13.71 3.86a2 2 0 0 0-3.42 0z"
  />,
  <line key="b" x1="12" y1="9" x2="12" y2="13.5" />,
  <line key="c" x1="12" y1="17" x2="12.01" y2="17" />,
]);

export const Clock = strokeIcon('clock', [
  <circle key="a" cx="12" cy="12" r="9" />,
  <polyline key="b" points="12 7 12 12 15 14" />,
]);

export const Mouse = strokeIcon('mouse', [
  <path key="a" d="M5 3l4.5 16 2.2-6.3L18 10z" />,
]);

export const Keyboard = strokeIcon('keyboard', [
  <rect key="a" x="2" y="6" width="20" height="12" rx="2" />,
  <path key="b" d="M6 10h.01M10 10h.01M14 10h.01M18 10h.01M7 14h10" />,
]);

export const Eye = strokeIcon('eye', [
  <path key="a" d="M1 12s4-7 11-7 11 7 11 7-4 7-11 7-11-7-11-7z" />,
  <circle key="b" cx="12" cy="12" r="3" />,
]);

export const EyeOff = strokeIcon('eyeOff', [
  <path
    key="a"
    d="M17.94 17.94A10 10 0 0 1 12 20c-7 0-11-8-11-8a18 18 0 0 1 5.06-5.94M9.9 4.24A9 9 0 0 1 12 4c7 0 11 8 11 8a18 18 0 0 1-2.16 3.19"
  />,
  <path key="b" d="M1 1l22 22" />,
]);

export const Copy = strokeIcon('copy', [
  <rect key="a" x="9" y="9" width="12" height="12" rx="2" />,
  <path key="b" d="M5 15H4a2 2 0 0 1-2-2V4a2 2 0 0 1 2-2h9a2 2 0 0 1 2 2v1" />,
]);

export const Trash = strokeIcon('trash', [
  <polyline key="a" points="3 6 5 6 21 6" />,
  <path
    key="b"
    d="M19 6v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6m3 0V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2"
  />,
]);

export const Dots = fillIcon('dots', [
  <circle key="a" cx="5" cy="12" r="1.6" />,
  <circle key="b" cx="12" cy="12" r="1.6" />,
  <circle key="c" cx="19" cy="12" r="1.6" />,
]);

export const Filter = strokeIcon('filter', [
  <polygon key="a" points="22 3 2 3 10 12.46 10 19 14 21 14 12.46 22 3" />,
]);

export const Globe = strokeIcon('globe', [
  <circle key="a" cx="12" cy="12" r="9" />,
  <line key="b" x1="3" y1="12" x2="21" y2="12" />,
  <path key="c" d="M12 3a14 14 0 0 1 0 18 14 14 0 0 1 0-18z" />,
]);

export const Monitor = strokeIcon('monitor', [
  <rect key="a" x="2" y="3" width="20" height="14" rx="2" />,
  <line key="b" x1="8" y1="21" x2="16" y2="21" />,
  <line key="c" x1="12" y1="17" x2="12" y2="21" />,
]);

export const Mail = strokeIcon('mail', [
  <rect key="a" x="2" y="4" width="20" height="16" rx="2" />,
  <path key="b" d="m22 7-10 6L2 7" />,
]);

export const User = strokeIcon('user', [
  <path key="a" d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2" />,
  <circle key="b" cx="12" cy="7" r="4" />,
]);

export const Send = strokeIcon('send', [
  <line key="a" x1="22" y1="2" x2="11" y2="13" />,
  <polygon key="b" points="22 2 15 22 11 13 2 9 22 2" />,
]);

export const Refresh = strokeIcon('refresh', [
  <polyline key="a" points="23 4 23 10 17 10" />,
  <polyline key="b" points="1 20 1 14 7 14" />,
  <path key="c" d="M3.51 9a9 9 0 0 1 14.85-3.36L23 10M1 14l4.64 4.36A9 9 0 0 0 20.49 15" />,
]);

export const TrendUp = strokeIcon('trendUp', [
  <polyline key="a" points="23 6 13.5 15.5 8.5 10.5 1 18" />,
  <polyline key="b" points="17 6 23 6 23 12" />,
]);

export const TrendDown = strokeIcon('trendDown', [
  <polyline key="a" points="23 18 13.5 8.5 8.5 13.5 1 6" />,
  <polyline key="b" points="17 18 23 18 23 12" />,
]);

export const Lock = strokeIcon('lock', [
  <rect key="a" x="3" y="11" width="18" height="11" rx="2" />,
  <path key="b" d="M7 11V7a5 5 0 0 1 10 0v4" />,
]);

export const Shield = strokeIcon('shield', [
  <path key="a" d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z" />,
]);

export const Mic = strokeIcon('mic', [
  <path key="a" d="M12 2a3 3 0 0 0-3 3v7a3 3 0 0 0 6 0V5a3 3 0 0 0-3-3z" />,
  <path key="b" d="M19 10v2a7 7 0 0 1-14 0v-2" />,
  <line key="c" x1="12" y1="19" x2="12" y2="23" />,
]);

export const Msg = strokeIcon('msg', [
  <path key="a" d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z" />,
]);

export const Code = strokeIcon('code', [
  <polyline key="a" points="16 18 22 12 16 6" />,
  <polyline key="b" points="8 6 2 12 8 18" />,
]);

export const Flag = strokeIcon('flag', [
  <path key="a" d="M4 15s1-1 4-1 5 2 8 2 4-1 4-1V3s-1 1-4 1-5-2-8-2-4 1-4 1z" />,
  <line key="b" x1="4" y1="22" x2="4" y2="15" />,
]);

export const Sliders = strokeIcon('sliders', [
  <line key="a" x1="4" y1="21" x2="4" y2="14" />,
  <line key="b" x1="4" y1="10" x2="4" y2="3" />,
  <line key="c" x1="12" y1="21" x2="12" y2="12" />,
  <line key="d" x1="12" y1="8" x2="12" y2="3" />,
  <line key="e" x1="20" y1="21" x2="20" y2="16" />,
  <line key="f" x1="20" y1="12" x2="20" y2="3" />,
  <line key="g" x1="1" y1="14" x2="7" y2="14" />,
  <line key="h" x1="9" y1="8" x2="15" y2="8" />,
  <line key="i" x1="17" y1="16" x2="23" y2="16" />,
]);

export const Pause2 = strokeIcon('pause2', [
  <circle key="a" cx="12" cy="12" r="9" />,
  <line key="b" x1="10" y1="9" x2="10" y2="15" />,
  <line key="c" x1="14" y1="9" x2="14" y2="15" />,
]);

export const Type = strokeIcon('type', [
  <polyline key="a" points="4 7 4 4 20 4 20 7" />,
  <line key="b" x1="9" y1="20" x2="15" y2="20" />,
  <line key="c" x1="12" y1="4" x2="12" y2="20" />,
]);

export const Link = strokeIcon('link', [
  <path key="a" d="M10 13a5 5 0 0 0 7.54.54l3-3a5 5 0 0 0-7.07-7.07l-1.72 1.71" />,
  <path key="b" d="M14 11a5 5 0 0 0-7.54-.54l-3 3a5 5 0 0 0 7.07 7.07l1.71-1.71" />,
]);

export const Smile = strokeIcon('smile', [
  <circle key="a" cx="12" cy="12" r="9" />,
  <path key="b" d="M8 14s1.5 2 4 2 4-2 4-2" />,
  <line key="c" x1="9" y1="9" x2="9.01" y2="9" />,
  <line key="d" x1="15" y1="9" x2="15.01" y2="9" />,
]);

export const Frown = strokeIcon('frown', [
  <circle key="a" cx="12" cy="12" r="9" />,
  <path key="b" d="M16 16s-1.5-2-4-2-4 2-4 2" />,
  <line key="c" x1="9" y1="9" x2="9.01" y2="9" />,
  <line key="d" x1="15" y1="9" x2="15.01" y2="9" />,
]);

export const Cpu = strokeIcon('cpu', [
  <rect key="a" x="5" y="5" width="14" height="14" rx="2" />,
  <rect key="b" x="9" y="9" width="6" height="6" />,
  <path key="c" d="M9 1v3M15 1v3M9 20v3M15 20v3M1 9h3M1 15h3M20 9h3M20 15h3" />,
]);

/* ── Aggregate record ───────────────────────────────────────────────────── */

/**
 * The `Icon` record — call sites do `<Icon.inbox size={16} />`. Keys match the
 * design's `window.HL_ICONS` names exactly.
 */
export const Icon = {
  inbox: Inbox,
  ticket: Ticket,
  widgets: Widgets,
  clusters: Clusters,
  org: Org,
  search: Search,
  chevDown: ChevDown,
  chevRight: ChevRight,
  arrowLeft: ArrowLeft,
  sun: Sun,
  moon: Moon,
  plus: Plus,
  x: X,
  check: Check,
  sparkle: Sparkle,
  play: Play,
  pause: Pause,
  camera: Camera,
  alert: Alert,
  clock: Clock,
  mouse: Mouse,
  keyboard: Keyboard,
  eye: Eye,
  eyeOff: EyeOff,
  copy: Copy,
  trash: Trash,
  dots: Dots,
  filter: Filter,
  globe: Globe,
  monitor: Monitor,
  mail: Mail,
  user: User,
  send: Send,
  refresh: Refresh,
  trendUp: TrendUp,
  trendDown: TrendDown,
  lock: Lock,
  shield: Shield,
  mic: Mic,
  msg: Msg,
  code: Code,
  flag: Flag,
  sliders: Sliders,
  pause2: Pause2,
  type: Type,
  link: Link,
  smile: Smile,
  frown: Frown,
  cpu: Cpu,
} as const;

/** Union of every available icon name, e.g. `'inbox' | 'ticket' | …`. */
export type IconName = keyof typeof Icon;

/** Convenience: the component type each `Icon[name]` resolves to. */
export type IconComponent = (typeof Icon)[IconName];
